import { Activity, Dispatch, FunctionComponent, memo, RefObject, SetStateAction } from 'react';

import styles from '../custom-select.module.css';

import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import Text from '@ui/text/text/text';

type Props = {
  idx: number;
  option: TReactSelectOption;
  handlePreventDefaultClick: (e: React.MouseEvent<HTMLElement>) => void;
  setSelectedOptions: Dispatch<SetStateAction<TReactSelectOption[]>>;
  selectRef: RefObject<HTMLDivElement | null>;
  RenderMenuOptionContent?: FunctionComponent<{ option: TReactSelectOption; idx: number; isSelected: boolean }>;
  isSelected: boolean;
  menuOptionSelectedIndicatorLabel?: string;
  onSelectOption?: (option: { option: TReactSelectOption; status: 'selected' | 'deselected' }) => void;
};

const CustomSelectMenuOption = memo(
  ({
    idx,
    option,
    handlePreventDefaultClick,
    setSelectedOptions,
    selectRef,
    RenderMenuOptionContent,
    isSelected,
    menuOptionSelectedIndicatorLabel,
    onSelectOption,
  }: Props) => {
    return (
      <div
        key={option.value}
        role="menuitem"
        aria-label={option.label}
        className={styles.menuOption}
        onClick={(e) => {
          handlePreventDefaultClick(e);
          setSelectedOptions((prev) => {
            if (prev.find((po) => po.value === option.value)) {
              return prev.filter((po) => po.value !== option.value);
            }
            return [...prev, option];
          });

          // let options append and then scroll
          setTimeout(() => {
            selectRef?.current?.scrollTo({
              left: selectRef?.current?.scrollWidth,
              behavior: 'smooth',
            });
          }, 0);

          const status: 'selected' | 'deselected' = isSelected ? 'deselected' : 'selected';
          onSelectOption?.({ option, status });
        }}
        data-menu-list-option
        data-menu-list-option-active="false"
        data-menu-list-option-idx={idx}
        data-menu-list-option-value={option.value}
        data-menu-list-option-selected={isSelected}
      >
        {RenderMenuOptionContent ? (
          <RenderMenuOptionContent option={option} idx={idx} isSelected={isSelected} />
        ) : (
          <>
            <Text clamp={1} wordBreak="break-all">
              {option.label}
            </Text>
            <Activity mode={isSelected && menuOptionSelectedIndicatorLabel ? 'visible' : 'hidden'}>
              <Text design="GlobalIcoText1" className="C2 margin-l-auto margin-r-0" whiteSpace="nowrap">
                {menuOptionSelectedIndicatorLabel}
              </Text>
            </Activity>
          </>
        )}
      </div>
    );
  }
);

CustomSelectMenuOption.displayName = 'CustomSelectMenuOption';

export default CustomSelectMenuOption;
